import React, { useState, useEffect } from 'react';

import { 
  FaUserShield, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaFilter,
  FaUsers,
  FaKey,
  FaEye
} from 'react-icons/fa';
import CompactActionBar from '../components/CompactActionBar';
import { roleService, Role, RoleFilters } from '../services/roleService';
import { usePageTitle } from '../hooks/usePageTitle';
import { PermissionGuard } from '../components/PermissionGuard';
import ConfirmDialog from '../components/ConfirmDialog';
import '../css/standard-components.css';
import { useNavigate } from 'react-router-dom';

const Roles: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRoles, setTotalRoles] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState<RoleFilters>({
    search: '',
    page: 1,
    limit: 100
  });

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  useEffect(() => {
    loadRoles();
  }, [filters]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.getRoles(filters);
      setRoles(response.roles);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setTotalRoles(response.pagination.total);
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (search: string) => {
    setFilters({ ...filters, search, page: 1 });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
  };

  const handleDelete = (role: Role) => {
    setRoleToDelete(role);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (roleToDelete) {
      try {
        setDeleting(true);
        await roleService.deleteRole(roleToDelete.id);
        loadRoles();
        setShowDeleteDialog(false);
        setRoleToDelete(null);
      } catch (error) {
        console.error('Error deleting role:', error);
        alert(error instanceof Error ? error.message : 'Failed to delete role');
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleEdit = (role: Role) => {
    navigate(`/auth/roles/edit?id=${role.id}`);
  };

  const handleView = (role: Role) => {
    navigate(`/auth/roles/view?id=${role.id}`);
  };

  return (
    <div className="page-container">
      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <input
            type="text"
            placeholder="Search roles by name or description..."
            value={filters.search || ''}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              padding: "6px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              fontSize: "13px",
              minWidth: "400px",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Show:</span>
            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value), page: 1 })}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>

          <PermissionGuard permission="role.create">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/auth/roles/add')}
              style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
            >
              <FaPlus /> Add Role
            </button>
          </PermissionGuard>
        </div>
      </CompactActionBar>

      {loading ? (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <div style={{fontSize: '16px', color: '#6b7280'}}>Loading roles...</div>
        </div>
      ) : (
        <div style={{ background: '#fff', margin: 0, padding: 0 }}>
          <table className="standard-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Role Name</th>
                <th>Description</th>
                <th>Permissions</th>
                <th>Users Assigned</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles?.map((role, index) => (
                <tr key={role.id}>
                  <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                    {(roles?.length || 0) - index}
                  </td>
                  <td>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <FaUserShield style={{color: '#3b82f6', fontSize: '16px'}} />
                      <span style={{fontWeight: '600', color: '#1f2937'}}>{role.name}</span>
                    </div>
                  </td>
                  <td>
                    <span style={{color: '#6b7280'}}>
                      {role.description || 'No description'}
                    </span>
                  </td>
                  <td>
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                      <FaKey style={{color: '#f59e0b', fontSize: '14px'}} />
                      <span style={{color: '#374151', fontSize: '14px'}}>{roleService.getRolePermissionCount(role)} permissions</span>
                    </div>
                  </td>
                  <td>
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                      <FaUsers style={{color: '#10b981', fontSize: '14px'}} />
                      <span style={{color: '#374151', fontSize: '14px'}}>{roleService.getRoleUserCount(role)} users</span>
                    </div>
                  </td>
                  <td>
                    <span style={{color: '#6b7280', fontSize: '14px'}}>
                      {new Date(role.createdAt).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <div style={{display: 'flex', gap: '8px'}}>
                      <PermissionGuard permission="role.view">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleView(role)}
                          title="View Role"
                        >
                          <FaEye />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission="role.edit">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEdit(role)}
                          title="Edit Role"
                        >
                          <FaEdit />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission="role.delete">
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(role)}
                          title="Delete Role"
                          disabled={roleService.getRoleUserCount(role) > 0}
                        >
                          <FaTrash />
                        </button>
                      </PermissionGuard>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {(roles?.length === 0 && !loading) && (
            <div style={{textAlign: 'center', padding: '60px', color: '#6b7280'}}>
              <FaUserShield style={{fontSize: '48px', marginBottom: '16px', opacity: '0.5'}} />
              <h3 style={{margin: '0 0 8px 0', color: '#374151'}}>No roles found</h3>
              <p style={{margin: '0 0 24px 0'}}>Create your first role to get started with role management.</p>
              <PermissionGuard permission="role.create">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/auth/roles/add')}
                >
                  <FaPlus /> Add Role
                </button>
              </PermissionGuard>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="content-section">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div style={{color: '#6b7280', fontSize: '14px'}}>
              Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalRoles)} of {totalRoles} roles
            </div>
            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={page}
                    className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${roleToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
        type="danger"
      />
    </div>
  );
};

export default Roles;
